/**
 * This utility function provides URL generation based on route names,
 * similar to how NamedLink component works but returns a URL string
 * instead of a React component.
 *
 * The `name` parameter should match a route in the flattened
 * routeConfiguration. The `params` parameter contains the route params
 * for the route path of the given route name.
 *
 * The `to` parameter is an object that can contain search and hash properties
 * which will be added to the generated URL.
 */
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { stringify } from '../../util/urlHelpers';
import getDomain from './getDomain';

/**
 * Creates a URL string for a named route with the given parameters
 *
 * @param {String} name - name of the route in routeConfiguration
 * @param {Object} params - params object for the named route
 * @param {Object} to - object containing search, hash, and state properties
 * @param {Object} routeConfiguration - route configuration object
 * @param {String} domain - domain to use for the URL (e.g., 'https://example.com')
 * @return {String} Complete URL string for the named route
 */
export const createNamedURL = (name, params = {}, to = {}, routeConfiguration, domain = '') => {
  if (!routeConfiguration) {
    throw new Error('Route configuration is required for createNamedURL');
  }

  const { search, hash } = to;
  const pathname = pathByRouteName(name, routeConfiguration, params);

  const searchString = search ? search : to.searchParams ? `?${stringify(to.searchParams)}` : '';
  const hashString = hash ? hash : '';

  // Ensure domain doesn't end with a slash
  const cleanDomain = domain.endsWith('/') ? domain.slice(0, -1) : domain;

  // Ensure pathname starts with a slash
  const cleanPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return `${cleanDomain}${cleanPathname}${searchString}${hashString}`;
};

/**
 * React hook that provides createNamedURL with the current route configuration
 *
 * @param {String} name - name of the route in routeConfiguration
 * @param {Object} params - params object for the named route
 * @param {Object} to - object containing search, hash, and state properties
 * @param {String} domain - domain to use for the URL (e.g., 'https://example.com')
 * @return {String} Complete URL string for the named route
 */
export const useNamedURL = (name, params = {}, to = {}, domain) => {
  const routeConfiguration = useRouteConfiguration();
  const resolvedDomain = domain === undefined ? getDomain() : domain;
  return createNamedURL(name, params, to, routeConfiguration, resolvedDomain);
};

export default createNamedURL;
